// Pantalla de visualización del SRT etiquetado (Fase 16).
//
// Descarga tagged.srt del bucket via PAR, lo parsea con parseSrt y muestra
// cada segmento como texto plano con su timestamp. No hay edición ni estado
// persistido — es solo lectura.
//
// Fase 43 — toggle original/corregido:
//   Si el pipelineStatus del meeting es "confirmed", se muestra un toggle
//   "Ver original" / "Ver corregido" en el header para alternar entre
//   tagged.srt y srt-confirmed.srt. El archivo a cargar se controla con
//   viewerSrtFileAtom.

import { useEffect, useRef, useState } from "react";
import { useAtomValue } from "jotai";
import styled from "styled-components";

import { currentProcessingIdAtom, viewerSrtFileAtom } from "../../state/atoms.ts";
import { getProcessingFile, fetchTextFromPar, getProcessing } from "../../platform/api.ts";
import { parseSrt } from "../../lib/parse-srt.ts";
import type { SrtSegment } from "../../lib/parse-srt.ts";
import { useNavigateTo, useSyncParams } from "../routing.ts";

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatTime(seconds: number): string {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = Math.floor(seconds % 60);
  const hh = String(h).padStart(2, "0");
  const mm = String(m).padStart(2, "0");
  const ss = String(s).padStart(2, "0");
  return `${hh}:${mm}:${ss}`;
}

// ---------------------------------------------------------------------------
// Tipos de estado interno
// ---------------------------------------------------------------------------

type ViewerState =
  | { kind: "loading" }
  | { kind: "error"; message: string }
  | { kind: "loaded"; segments: SrtSegment[]; videoUrl: string | null; isConfirmed: boolean };

// ---------------------------------------------------------------------------
// Componente principal
// ---------------------------------------------------------------------------

export function ViewerScreen() {
  const processingId = useAtomValue(currentProcessingIdAtom);
  const navigateTo = useNavigateTo();
  useSyncParams();
  const viewerFile = useAtomValue(viewerSrtFileAtom);
  const [state, setState] = useState<ViewerState>({ kind: "loading" });

  // Video playback
  const videoRef = useRef<HTMLVideoElement>(null);
  const stopAtRef = useRef<number | null>(null);
  const [playingIndex, setPlayingIndex] = useState<number | null>(null);

  useEffect(() => {
    if (!processingId) return;

    let cancelled = false;

    async function cargar() {
      try {
        // SRT y meta en paralelo. El meta puede fallar silencioso — en ese
        // caso no hay video pero los segmentos siguen mostrándose igual.
        const [srtInfo, metaResult] = await Promise.all([
          getProcessingFile(processingId!, viewerFile),
          getProcessing(processingId!).catch((err) => {
            console.warn("[ViewerScreen] getProcessing falló:", err);
            return null;
          }),
        ]);

        const texto = await fetchTextFromPar(srtInfo.downloadUrl);
        const segments = parseSrt(texto);

        const isConfirmed = metaResult?.pipelineStatus === "confirmed";

        // Intentar cargar video si el procesamiento tiene audio.mp4.
        let videoUrl: string | null = null;
        const audioFilename = metaResult?.audioFilename ?? "audio.wav";
        if (audioFilename === "audio.mp4") {
          try {
            const videoInfo = await getProcessingFile(processingId!, "audio.mp4");
            videoUrl = videoInfo.downloadUrl;
          } catch (err) {
            console.warn("[ViewerScreen] audio.mp4 no encontrado:", err);
          }
        }

        if (!cancelled) {
          setState({ kind: "loaded", segments, videoUrl, isConfirmed });
        }
      } catch (err) {
        if (!cancelled) {
          setState({
            kind: "error",
            message: err instanceof Error ? err.message : "Error desconocido",
          });
        }
      }
    }

    void cargar();

    return () => {
      cancelled = true;
    };
  }, [processingId, viewerFile]);

  function handleVolver() {
    navigateTo.meetings();
  }

  function handlePlaySegment(seg: SrtSegment) {
    const video = videoRef.current;
    if (!video) return;

    stopAtRef.current = seg.endTime;
    video.currentTime = seg.startTime;
    setPlayingIndex(seg.index);
    void video.play();
  }

  function handleTimeUpdate() {
    const video = videoRef.current;
    if (!video || stopAtRef.current === null) return;
    if (video.currentTime >= stopAtRef.current) {
      video.pause();
      stopAtRef.current = null;
      setPlayingIndex(null);
    }
  }

  function handleVideoPause() {
    stopAtRef.current = null;
    setPlayingIndex(null);
  }

  const hasVideo = state.kind === "loaded" && state.videoUrl !== null;

  return (
    <Container $hasVideo={hasVideo}>
      <Header>
        <BackButton onClick={handleVolver}>← Volver</BackButton>
        <PageTitle>
          {viewerFile === "srt-confirmed.srt"
            ? "SRT confirmado"
            : "SRT etiquetado"}
        </PageTitle>
        {state.kind === "loaded" && state.isConfirmed && (
          <ToggleGroup>
            <ToggleOption
              $active={viewerFile === "tagged.srt"}
              onClick={() => processingId && navigateTo.viewer(processingId, "tagged.srt")}
            >
              Original
            </ToggleOption>
            <ToggleOption
              $active={viewerFile === "srt-confirmed.srt"}
              onClick={() => processingId && navigateTo.viewer(processingId, "srt-confirmed.srt")}
            >
              Corregido
            </ToggleOption>
          </ToggleGroup>
        )}
      </Header>

      {state.kind === "loading" && (
        <StatusMessage>Cargando transcripción…</StatusMessage>
      )}

      {state.kind === "error" && (
        <ErrorBox>
          <ErrorTitle>No se pudo cargar el archivo</ErrorTitle>
          <ErrorDetail>{state.message}</ErrorDetail>
          <BackButton onClick={handleVolver}>← Volver a la lista</BackButton>
        </ErrorBox>
      )}

      {state.kind === "loaded" && (
        <>
          <SegmentCount>{state.segments.length} segmentos</SegmentCount>
          <SegmentList>
            {state.segments.map((seg) => (
              <SegmentRow key={seg.index} $hasVideo={hasVideo}>
                <SegmentTimestamp>
                  {formatTime(seg.startTime)} → {formatTime(seg.endTime)}
                </SegmentTimestamp>
                <SegmentText>{seg.lines.join(" ")}</SegmentText>
                {hasVideo && (
                  <PlayButton
                    onClick={() => handlePlaySegment(seg)}
                    $active={playingIndex === seg.index}
                    title="Reproducir este segmento"
                    aria-label="Reproducir segmento"
                  >
                    {playingIndex === seg.index ? "◼" : "▶"}
                  </PlayButton>
                )}
              </SegmentRow>
            ))}
          </SegmentList>
        </>
      )}

      {/* Panel de video fijo — solo cuando hay MP4 */}
      {state.kind === "loaded" && state.videoUrl !== null && (
        <VideoPanel>
          <video
            ref={videoRef}
            src={state.videoUrl}
            onTimeUpdate={handleTimeUpdate}
            onPause={handleVideoPause}
            controls
            style={{ width: "100%", height: "100%", objectFit: "contain", display: "block" }}
          />
        </VideoPanel>
      )}
    </Container>
  );
}

// ---------------------------------------------------------------------------
// Estilos
// ---------------------------------------------------------------------------

const VIDEO_PANEL_HEIGHT = 260;

const Container = styled.div<{ $hasVideo: boolean }>`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.space.lg};
  padding-bottom: ${({ $hasVideo }) => ($hasVideo ? `${VIDEO_PANEL_HEIGHT + 16}px` : "0")};
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.space.md};
`;

const BackButton = styled.button`
  background: none;
  border: 1px solid ${({ theme }) => theme.color.border};
  border-radius: ${({ theme }) => theme.radius.sm};
  color: ${({ theme }) => theme.color.textMuted};
  cursor: pointer;
  font-size: ${({ theme }) => theme.font.size.sm};
  padding: ${({ theme }) => theme.space.xs} ${({ theme }) => theme.space.sm};
  white-space: nowrap;

  &:hover {
    background: ${({ theme }) => theme.color.surface};
    color: ${({ theme }) => theme.color.textPrimary};
  }
`;

const PageTitle = styled.h1`
  font-size: ${({ theme }) => theme.font.size.xl};
  font-weight: 600;
  margin: 0;
`;

const StatusMessage = styled.p`
  color: ${({ theme }) => theme.color.textMuted};
  font-size: ${({ theme }) => theme.font.size.md};
  margin: 0;
`;

const ErrorBox = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.space.sm};
  background: ${({ theme }) => theme.color.errorMuted};
  border: 1px solid ${({ theme }) => theme.color.error};
  border-radius: ${({ theme }) => theme.radius.md};
  padding: ${({ theme }) => theme.space.lg};
`;

const ErrorTitle = styled.p`
  color: ${({ theme }) => theme.color.error};
  font-weight: 600;
  margin: 0;
`;

const ErrorDetail = styled.p`
  color: ${({ theme }) => theme.color.textMuted};
  font-size: ${({ theme }) => theme.font.size.sm};
  margin: 0;
`;

const SegmentCount = styled.p`
  color: ${({ theme }) => theme.color.textMuted};
  font-size: ${({ theme }) => theme.font.size.sm};
  margin: 0;
`;

const SegmentList = styled.div`
  display: flex;
  flex-direction: column;
`;

const SegmentRow = styled.div<{ $hasVideo: boolean }>`
  display: grid;
  grid-template-columns: ${({ $hasVideo }) =>
    $hasVideo ? "180px 1fr 32px" : "180px 1fr"};
  gap: ${({ theme }) => theme.space.md};
  align-items: center;
  padding: ${({ theme }) => theme.space.sm} 0;
  border-bottom: 1px solid ${({ theme }) => theme.color.border};

  &:last-child {
    border-bottom: none;
  }
`;

const SegmentTimestamp = styled.span`
  color: ${({ theme }) => theme.color.textMuted};
  font-family: monospace;
  font-size: ${({ theme }) => theme.font.size.sm};
  white-space: nowrap;
`;

const SegmentText = styled.span`
  color: ${({ theme }) => theme.color.textPrimary};
  font-size: ${({ theme }) => theme.font.size.md};
  line-height: 1.5;
`;

const PlayButton = styled.button<{ $active: boolean }>`
  background: ${({ $active, theme }) => ($active ? theme.color.accent : "none")};
  border: 1px solid ${({ $active, theme }) => ($active ? theme.color.accent : theme.color.border)};
  border-radius: ${({ theme }) => theme.radius.sm};
  color: ${({ $active, theme }) => ($active ? "#fff" : theme.color.textMuted)};
  cursor: pointer;
  font-size: 10px;
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: background 0.15s, color 0.15s, border-color 0.15s;

  &:hover {
    background: ${({ theme }) => theme.color.accent};
    border-color: ${({ theme }) => theme.color.accent};
    color: #fff;
  }
`;

const ToggleGroup = styled.div`
  display: flex;
  border: 1px solid ${({ theme }) => theme.color.border};
  border-radius: ${({ theme }) => theme.radius.sm};
  overflow: hidden;
  flex-shrink: 0;
`;

const ToggleOption = styled.button<{ $active: boolean }>`
  background: ${({ $active, theme }) =>
    $active ? theme.color.accent : "transparent"};
  border: none;
  color: ${({ $active, theme }) =>
    $active ? "#fff" : theme.color.textMuted};
  cursor: pointer;
  font-size: ${({ theme }) => theme.font.size.sm};
  padding: 4px 12px;
  transition: background 0.15s, color 0.15s;

  &:hover {
    background: ${({ $active, theme }) =>
      $active ? theme.color.accent : theme.color.surface};
  }

  &:not(:last-child) {
    border-right: 1px solid ${({ theme }) => theme.color.border};
  }
`;

const VideoPanel = styled.div`
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  height: ${VIDEO_PANEL_HEIGHT}px;
  background: #000;
  border-top: 1px solid ${({ theme }) => theme.color.border};
  z-index: 100;
`;

